import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { FiAward, FiX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getNominations, updateNomination } from '../services/api';

const STATUSES = ['Pending', 'Shortlisted', 'Selected', 'Archived'];
const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', fg: '#92400e' },
  Shortlisted: { bg: '#dbeafe', fg: '#1e40af' },
  Selected: { bg: '#dcfce7', fg: '#166534' },
  Archived: { bg: '#f1f5f9', fg: '#475569' },
};

function NominationsAdmin() {
  const { user } = useAuth();
  const [nominations, setNominations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const isAdmin = !!user?.can_manage_payroll;

  useEffect(() => {
    if (!isAdmin) return;
    loadNominations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, isAdmin]);

  const loadNominations = async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'All' ? {} : { status: statusFilter };
      const res = await getNominations(params);
      setNominations(res.data.nominations || []);
    } catch (err) {
      console.error('Failed to load nominations', err);
    } finally {
      setLoading(false);
    }
  };

  if (user && !isAdmin) {
    return <Navigate to="/nominations" replace />;
  }

  const handleUpdated = (updated) => {
    setSelected(updated);
    loadNominations();
  };

  const counts = nominations.reduce((acc, n) => {
    acc[n.status] = (acc[n.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FiAward /> Recognition Admin
        </h1>
        <p style={{ color: '#666', marginTop: '6px' }}>
          Review submitted nominations and shortlist, select, or archive them.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {['All', ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '8px 14px', borderRadius: '6px',
              border: '1px solid ' + (statusFilter === s ? '#1a73e8' : '#d1d5db'),
              backgroundColor: statusFilter === s ? '#1a73e8' : 'white',
              color: statusFilter === s ? 'white' : '#333',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            {s}{statusFilter === s && counts[s] !== undefined ? ` (${counts[s] || nominations.length})` : ''}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: '16px' }}>
        <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '30px', color: '#666' }}>Loading…</div>
          ) : nominations.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
              No nominations in this view.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', textAlign: 'left' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Nominee</th>
                  <th style={th}>Nominator</th>
                  <th style={th}>Category</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {nominations.map((n) => {
                  const colors = STATUS_COLORS[n.status] || STATUS_COLORS.Pending;
                  const isSelected = selected?.id === n.id;
                  return (
                    <tr
                      key={n.id}
                      onClick={() => setSelected(n)}
                      style={{
                        borderTop: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eff6ff' : 'white'
                      }}
                    >
                      <td style={td}>{new Date(n.created_at + 'Z').toLocaleDateString()}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{n.nominee_name}</td>
                      <td style={td}>{n.nominator_name}</td>
                      <td style={td}>{n.category}</td>
                      <td style={td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                          fontWeight: 600, backgroundColor: colors.bg, color: colors.fg
                        }}>
                          {n.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <ReviewPanel
            key={selected.id}
            nomination={selected}
            onClose={() => setSelected(null)}
            onUpdated={handleUpdated}
          />
        )}
      </div>
    </div>
  );
}

function ReviewPanel({ nomination, onClose, onUpdated }) {
  const [status, setStatus] = useState(nomination.status);
  const [notes, setNotes] = useState(nomination.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const dirty =
    status !== nomination.status ||
    (notes || '').trim() !== (nomination.admin_notes || '').trim();

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await updateNomination(nomination.id, {
        status,
        admin_notes: notes,
      });
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update nomination');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', alignSelf: 'start', position: 'sticky', top: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0 }}>Review</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}><FiX /></button>
      </div>

      <Detail label="Nominee" value={nomination.nominee_name} />
      <Detail label="Nominated by" value={nomination.nominator_name} />
      <Detail label="Category" value={nomination.category} />
      <Detail
        label="Submitted"
        value={new Date(nomination.created_at + 'Z').toLocaleString()}
      />
      {nomination.project_or_job_name && (
        <Detail label="Project / Job" value={nomination.project_or_job_name} />
      )}
      {/* date_of_action is a db.Date, so it arrives date-only. A bare new Date()
          reads that as UTC midnight and renders the day BEFORE in Eastern -- the
          same class of bug that showed a client "Invalid Date" on the portal.
          Local noon pins the calendar day in any zone. (created_at/reviewed_at
          are real timestamps and correctly keep their 'Z'.) */}
      {nomination.date_of_action && (
        <Detail label="Date of action"
          value={new Date(nomination.date_of_action + 'T12:00:00').toLocaleDateString()} />
      )}

      <div style={{ marginTop: '12px' }}>
        <div style={labelStyle}>Reason</div>
        <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '14px', color: '#222', whiteSpace: 'pre-wrap' }}>
          {nomination.reason}
        </div>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={labelStyle}>Status</div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={input}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={labelStyle}>Private admin notes</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder="Notes visible only to admins…"
        />
      </div>

      {nomination.reviewed_by_name && (
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
          Last reviewed by {nomination.reviewed_by_name}
          {nomination.reviewed_at && ` on ${new Date(nomination.reviewed_at + 'Z').toLocaleString()}`}
        </div>
      )}

      {error && (
        <div style={{ marginTop: '10px', padding: '8px 10px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button onClick={onClose} style={btnSecondary}>Close</button>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          style={{ ...btnPrimary, opacity: dirty && !saving ? 1 : 0.5, cursor: dirty && !saving ? 'pointer' : 'not-allowed' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '14px', color: '#222' }}>{value}</div>
    </div>
  );
}

const th = { padding: '10px 14px', fontSize: '12px', color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '10px 14px', fontSize: '14px', color: '#222', verticalAlign: 'top' };
const labelStyle = { fontSize: '12px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' };
const input = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', backgroundColor: '#1a73e8', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };

export default NominationsAdmin;
